import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = "/logo-full.svg";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const result = await login(email, password);
        setLoading(false);
        if (result.success) {
            toast.success('Welcome back!');
            const role = result.user?.role;
            navigate(role === 'admin' ? '/admin' : '/dashboard');
        } else {
            toast.error(result.error);
        }
    };

    return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <img src={LOGO_URL} alt="Food Safe Hub" className="mx-auto mb-4" style={{ width: '75%', maxWidth: '320px' }} />
                    <p className="text-slate-400 text-sm mt-1">Safer food, better business</p>
                </div>

                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-5">
                        <Shield className="h-5 w-5 brand-icon" />
                        <h2 className="text-lg font-semibold text-slate-200">Sign In</h2>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <Label htmlFor="email" className="text-slate-300">Email</Label>
                            <Input id="email" type="email" data-testid="login-email-input" value={email}
                                onChange={(e) => setEmail(e.target.value)} required
                                className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="you@example.com" />
                        </div>
                        <div>
                            <Label htmlFor="password" className="text-slate-300">Password</Label>
                            <div className="relative mt-1">
                                <Input id="password" type={showPassword ? 'text' : 'password'} data-testid="login-password-input"
                                    value={password} onChange={(e) => setPassword(e.target.value)} required
                                    className="bg-slate-800 border-slate-700 text-slate-100 pr-10" placeholder="Enter password" />
                                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                                    onClick={() => setShowPassword(!showPassword)}>
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>
                        <Button type="submit" data-testid="login-submit-button" disabled={loading}
                            className="w-full brand-btn font-semibold">
                            {loading ? 'Signing in...' : 'Sign In'}
                        </Button>
                    </form>

                    <div className="mt-5 pt-4 border-t border-slate-800 text-center">
                        <p className="text-sm text-slate-400">
                            Need an account for your business?{' '}
                            <Link to="/register" className="brand-text hover:opacity-80 font-medium" data-testid="register-link">
                                Register here
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
